import { useEffect, useRef, useState, useCallback } from 'react';
import AppHeader from './compo/app_header';
import AppFooter from './compo/app_footer';
import Tabbar from './compo/tabbar';
import MainMap from './tab/main_map';
import MainList from './tab/main_list';
import MainConsultation from './tab/main_consultation';
import Filter from './tab/filter';
import InterestedPropertyModal from './compo/interested_property_modal';
import NoticePopupModal from './compo/notice_popup_modal';
import { focusOnPropertyLocation } from './utils/mapFocus';
import { useAuth, ROLES } from './contexts/AuthContext';
import { fetchConsultations, fetchLatestNotice } from './utils/api';
import './App.css';

const RANGE_KEYS = ['층수', '총층수', '평수', '보증금', '월세', '관리비', '권리비'];

const createInitialFilters = () => {
  const rangeFilters = RANGE_KEYS.reduce((acc, key) => {
    acc[key] = { min: '', max: '' };
    return acc;
  }, {});

  return {
    상권: [],
    용도: [],
    엘베: '',
    화장실: '',
    관심매물손님: undefined,
    이름검색: '',
    ...rangeFilters
  };
};

const MIN_CONTENT_HEIGHT = 360;

// 확인한 공지사항 ID를 localStorage에서 가져오기
const getReadNoticeIds = () => {
  try {
    const stored = localStorage.getItem('readNoticeIds');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('확인한 공지사항 ID 읽기 오류:', error);
    return [];
  }
};

// 확인한 공지사항 ID를 localStorage에 저장
const saveReadNoticeId = (noticeId) => {
  try {
    const readIds = getReadNoticeIds();
    if (!readIds.includes(noticeId)) {
      readIds.push(noticeId);
      localStorage.setItem('readNoticeIds', JSON.stringify(readIds));
    }
  } catch (error) {
    console.error('확인한 공지사항 ID 저장 오류:', error);
  }
};

function App() {
  const { user, role, loading } = useAuth();
  const isVisitor = role === ROLES.VISITOR;
  const isGuest = role === ROLES.GUEST;
  
  // 모든 hooks는 항상 같은 순서로 호출되어야 함 (조건부 호출 금지)
  const [activeTab, setActiveTab] = useState('map');
  const [filters, setFilters] = useState(createInitialFilters());
  const [contentHeight, setContentHeight] = useState(0);
  const mapRef = useRef(null);
  const headerRef = useRef(null);
  const filterRef = useRef(null);
  const tabbarRef = useRef(null);
  const footerRef = useRef(null);
  const [isInterestedPropertyModalOpen, setIsInterestedPropertyModalOpen] = useState(false);
  const [propertyForInterested, setPropertyForInterested] = useState(null);
  const [consultations, setConsultations] = useState([]); // 상담 데이터 (관심 매물 필터링용)
  const [isScreenshotMode, setIsScreenshotMode] = useState(false); // 스크린샷 모드
  const [latestNotice, setLatestNotice] = useState(null); // 최신 공지사항
  const [isNoticePopupOpen, setIsNoticePopupOpen] = useState(false); // 공지사항 팝업 열림 여부
  
  // 상담 데이터 로드 (관심 매물 필터링용)
  const loadConsultations = useCallback(async () => {
    if (isVisitor) {
      setConsultations([]);
      return;
    }
    try {
      const data = await fetchConsultations(role);
      setConsultations(data);
    } catch (error) {
      console.error('상담 데이터 로드 실패:', error);
      setConsultations([]);
    }
  }, [role, isVisitor]);

  useEffect(() => {
    if (!loading) {
      loadConsultations();
    }
  }, [loading, loadConsultations]);

  // 로그인 시 최신 공지사항 확인
  useEffect(() => {
    // visitor는 공지사항을 보지 않음, 로딩 중이거나 사용자가 없으면 리턴
    if (isVisitor || !user || loading) {
      return;
    }

    const checkLatestNotice = async () => {
      try {
        console.log('[공지사항] 최신 공지사항 확인 시작', { user: user?.email, role });
        const notice = await fetchLatestNotice(role);
        console.log('[공지사항] 최신 공지사항 조회 결과', notice);
        
        if (!notice) {
          console.log('[공지사항] 공지사항 없음');
          return;
        }

        // 이미 확인한 공지사항인지 확인
        const readIds = getReadNoticeIds();
        console.log('[공지사항] 읽은 공지사항 ID 목록', readIds);
        
        if (readIds.includes(notice.id)) {
          console.log('[공지사항] 이미 확인한 공지사항', notice.id);
          return;
        }

        // 최신 공지사항이 있고 아직 확인하지 않았다면 팝업 표시
        // 약간의 지연을 두어 로그인 후 UI가 안정화된 후 팝업 표시
        console.log('[공지사항] 팝업 표시 예정', notice.id);
        setTimeout(() => {
          setLatestNotice(notice);
          setIsNoticePopupOpen(true);
        }, 1000);
      } catch (error) {
        console.error('[공지사항] 최신 공지사항 확인 오류:', error);
      }
    };

    // user가 있고 loading이 false일 때만 실행
    checkLatestNotice();
  }, [user, role, loading, isVisitor]);

  // 공지사항 확인 처리
  const handleNoticeConfirm = (noticeId) => {
    saveReadNoticeId(noticeId);
    setIsNoticePopupOpen(false);
    setLatestNotice(null);
  };

  // 관심 매물 등록 성공 시 상담 데이터 새로고침
  useEffect(() => {
    const handleRefresh = () => {
      loadConsultations();
    };
    window.addEventListener('consultationRefresh', handleRefresh);
    return () => {
      window.removeEventListener('consultationRefresh', handleRefresh);
    };
  }, [loadConsultations]);
  
  // visitor와 guest는 지도 탭만 사용 가능, guest는 상담 탭도 비활성화
  useEffect(() => {
    if (isVisitor && activeTab !== 'map') {
      setActiveTab('map');
    }
    if (isGuest && activeTab === 'consultation') {
      setActiveTab('map');
    }
  }, [isVisitor, isGuest, activeTab]);
  
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleFilterReset = () => {
    setFilters(createInitialFilters());
  };

  // 홈 화면으로 새로고침 (헤더 클릭 시)
  const handleHomeClick = () => {
    // 지도 탭으로 이동
    setActiveTab('map');
    // 필터 초기화
    setFilters(createInitialFilters());
    // 지도 초기 위치로 리셋
    setTimeout(() => {
      if (mapRef.current?.resetMap) {
        mapRef.current.resetMap();
      }
    }, 100);
  };

  // 관심 매물 등록 핸들러
  const handleAddInterestedProperty = (property) => {
    setPropertyForInterested(property);
    setIsInterestedPropertyModalOpen(true);
  };

  // 관심 매물 등록 성공 시 콜백
  const handleInterestedPropertySuccess = () => {
    // 상담 탭이 활성화되어 있으면 상담 데이터를 새로고침하도록 이벤트 발생
    if (activeTab === 'consultation') {
      window.dispatchEvent(new CustomEvent('consultationRefresh'));
    }
  };

  // 매물 카드에서 지도 클릭 시 호출되는 함수 (공통 유틸리티 함수 사용)
  const handleFocusOnMap = (property) => {
    focusOnPropertyLocation(property, {
      setActiveTab,
      currentTab: activeTab,
      mapRef,
      onSuccess: () => {
        console.log('지도 포커스 성공:', property);
      }
    });
  };

  useEffect(() => {
    const calcHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const filterHeight = filterRef.current?.offsetHeight || 0;
      const tabHeight = tabbarRef.current?.offsetHeight || 0;
      const footerHeight = footerRef.current?.offsetHeight || 0;
      const windowHeight = window.innerHeight;
      const available = windowHeight - headerHeight - filterHeight - tabHeight - footerHeight;
      setContentHeight(Math.max(MIN_CONTENT_HEIGHT, available));
    };

    const resizeObserver = new ResizeObserver(calcHeight);
    [headerRef.current, filterRef.current, tabbarRef.current, footerRef.current].forEach((el) => {
      if (el) resizeObserver.observe(el);
    });

    window.addEventListener('resize', calcHeight);
    calcHeight();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calcHeight);
    };
  }, []);

  // ESC 키로 스크린샷 모드 종료
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isScreenshotMode) {
        setIsScreenshotMode(false);
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isScreenshotMode]);

  // Firebase 인증 상태 확인 중일 때는 로딩 표시
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  // 스크린샷 모드일 때는 헤더, 필터, 탭바 숨기기
  if (isScreenshotMode) {
    return (
      <div className="fixed inset-0 bg-black z-[9999] flex items-center justify-center">
        <div className="w-full h-full" style={{ aspectRatio: '16/9', maxWidth: '100vw', maxHeight: '100vh' }}>
          <MainMap
            ref={mapRef}
            filters={filters}
            consultations={consultations}
            availableHeight={window.innerHeight}
            onFocusMap={handleFocusOnMap}
            onAddInterestedProperty={handleAddInterestedProperty}
            isScreenshotMode={true}
            onExitScreenshotMode={() => setIsScreenshotMode(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <div ref={headerRef}>
        <AppHeader 
          onHomeClick={handleHomeClick}
        />
      </div>
      <div ref={filterRef}>
        <Filter
          filters={filters}
          onFilterChange={handleFilterChange}
          onReset={handleFilterReset}
        />
      </div>
      <div ref={tabbarRef}>
        <Tabbar activeTab={activeTab} setActiveTab={setActiveTab} />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        {activeTab === 'map' && (
          <MainMap
            ref={mapRef}
            filters={filters}
            consultations={consultations}
            availableHeight={contentHeight}
            onFocusMap={handleFocusOnMap}
            onAddInterestedProperty={handleAddInterestedProperty}
            isScreenshotMode={isScreenshotMode}
            onEnterScreenshotMode={() => setIsScreenshotMode(true)}
            onExitScreenshotMode={() => setIsScreenshotMode(false)}
            onSwitchToTab={setActiveTab}
          />
        )}
        {activeTab === 'list' && (
          <MainList
            filters={filters}
            consultations={consultations}
            availableHeight={contentHeight}
            onFocusMap={handleFocusOnMap}
            onAddInterestedProperty={handleAddInterestedProperty}
          />
        )}
        {activeTab === 'consultation' && (
          <MainConsultation
            availableHeight={contentHeight}
            onAddInterestedProperty={handleAddInterestedProperty}
            onFocusMap={handleFocusOnMap}
          />
        )}
      </div>

      {/* 관심 매물 등록 모달 (전역) */}
      <InterestedPropertyModal
        isOpen={isInterestedPropertyModalOpen}
        onClose={() => {
          setIsInterestedPropertyModalOpen(false);
          setPropertyForInterested(null);
        }}
        property={propertyForInterested}
        onSuccess={handleInterestedPropertySuccess}
      />

      {/* 공지사항 팝업 모달 */}
      <NoticePopupModal
        notice={latestNotice}
        isOpen={isNoticePopupOpen}
        onConfirm={handleNoticeConfirm}
      />
      <div ref={footerRef}>
        <AppFooter />
      </div>
    </div>
  );
  
}

export default App;
